/**
 * Los errores de Firestore, en castellano.
 *
 * El SDK habla en inglés y con vocabulario de base de datos: «No document to
 * update», «Missing or insufficient permissions». Eso aparecía tal cual en
 * pantalla, y quien está cerrando un estado de pago no tiene por qué
 * traducir ni saber qué es un documento.
 *
 * Cada mensaje dice qué pasó y, cuando corresponde, qué hacer.
 */

/** Códigos que devuelve el SDK de Firestore. */
const POR_CODIGO: Record<string, string> = {
  'not-found': 'El registro ya no existe: alguien lo borró desde otro dispositivo.',
  'permission-denied':
    'Tu usuario no tiene permiso para esto. Si crees que debería tenerlo, habla con el administrador.',
  unauthenticated: 'Se cerró la sesión. Vuelve a entrar.',
  unavailable: 'Sin conexión con la base de datos. Revisa la red e inténtalo de nuevo.',
  'deadline-exceeded': 'La operación tardó demasiado. Inténtalo de nuevo.',
  'resource-exhausted':
    'Se acabó la cuota diaria de la base de datos. Se renueva a medianoche; inténtalo mañana.',
  'failed-precondition':
    'A la base de datos le falta un índice para esta consulta. Avísame y lo publico.',
  aborted: 'Otro cambio ocurrió al mismo tiempo. Vuelve a intentarlo.',
  'already-exists': 'Ya existe un registro con ese identificador.',
  cancelled: 'La operación se canceló antes de terminar.',
};

/** Mensajes en inglés que el SDK arma a mano, sin código aprovechable. */
const POR_TEXTO: [RegExp, string][] = [
  [
    /no document to update/i,
    'El registro que se intentaba actualizar ya no existe: probablemente se borró después.',
  ],
  [/missing or insufficient permissions/i, POR_CODIGO['permission-denied']!],
  [/quota|resource[- ]exhausted/i, POR_CODIGO['resource-exhausted']!],
  [
    /client is offline|failed to get document because the client is offline/i,
    POR_CODIGO.unavailable!,
  ],
  [/the query requires an index/i, POR_CODIGO['failed-precondition']!],
];

/**
 * Traduce un error de Firestore. Si no lo reconoce devuelve `respaldo`, que
 * conviene que sea una frase del contexto —«no se pudo cerrar el estado de
 * pago»— y no el texto crudo del SDK.
 */
export function mensajeFirestore(e: unknown, respaldo: string): string {
  const codigo = (e as { code?: string })?.code ?? '';
  const corto = codigo.replace(/^firestore\//, '');
  if (POR_CODIGO[corto]) return POR_CODIGO[corto];

  const texto = e instanceof Error ? e.message : String(e ?? '');
  for (const [re, msg] of POR_TEXTO) if (re.test(texto)) return msg;

  return respaldo;
}
